import { FollowableUser, News } from 'js/social/shared/types';
import { FollowSuggestion } from 'js/social/shared/big_cards/BulkFollowSuggestion';

/**
 * Get a human-readable description for a raw byte count.
 * @param bytes The number of bytes.
 * @param fractionDigits The number of fractional digits.
 */
export function humanReadableBytes(bytes: number, fractionDigits = 0) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];

  let unitIndex = 0;
  let count = bytes;
  while (Math.round(count) >= 1000) {
    unitIndex = Math.min(unitIndex + 1, units.length - 1);
    count /= 1000;
  }

  return `${count.toFixed(fractionDigits)} ${units[unitIndex]}`;
}

export function extractNumericIdFromOpenalexId(openalexId?: string | number) {
  return `${openalexId}`.replace('https://openalex.org/W', '');
}

/**
 * Utility method to scroll a child element into view. This utility
 * is needed because scrollIntoView() doesn't work well with modals.
 * @param parent The parent scrollable container.
 * @param child The child.
 */
export function scrollChildIntoView(
  parent: HTMLDivElement | null,
  child: HTMLDivElement | HTMLButtonElement | null,
) {
  if (parent && child) {
    const visibleTop = child.offsetTop - parent.scrollTop;
    if (visibleTop + child.offsetHeight > parent.offsetHeight) {
      // Past the bottom
      parent.scrollTo({
        top: child.offsetTop + child.offsetHeight - parent.offsetHeight,
      });
    } else if (visibleTop < 0) {
      // Above the top
      parent.scrollTo({ top: child.offsetTop });
    }
  }
}

/**
 * Convert a followable user to a suggestion for follow lists.
 */
export const followableUserToSuggestion = (
  user: FollowableUser,
): FollowSuggestion => {
  return {
    id: user.id,
    displayName: user.displayName,
    photoUrl: user.photo,
    universityName: user.university?.displayName,
    departmentName: user.department?.displayName,
  };
};

/**
 * Determine if a news item is a recommendation.
 * @param news The news item.
 */
export const isRecommendation = (news: News) => {
  return (
    news.newsType === 'openalex_work' ||
    (news.newsType === 'paper_update' && news.reason?.type === 'recommended')
  );
};
